import cx from 'classnames';
import styles from './HeroModule.module.scss';

import React from 'react';

import {
  HeroCtaVariant,
  HeroImageVariant,
  TextAlignment,
  VerticalPosition,
} from 'api/contentful/contentTypes.cloverdotcom';
import { Grid } from 'components/Grid/Grid';
import { TextV2 } from 'components/TextV2/TextV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { toEntry, AppType, Image as ImageType, WithCMS } from 'services/cms/typesUtil';
import { componentKey } from 'utils/EntryUtil';
import { ContentLinkV2 } from '../ContentLinkV2/ContentLinkV2';
import { HeroModuleBanner } from './HeroModuleBanner/HeroModuleBanner';
import { HeroModuleImage } from './HeroModuleImage/HeroModuleImage';

type HeroModuleProps = AppType.Component<{
  headline: string;
  subhead?: string;
  imageLayout: string;
  desktopTabletHeroImage?: ImageType;
  desktopTabletProductImage?: ImageType;
  desktopTabletHeroSecondaryImage?: ImageType;
  mobileHeroImage?: ImageType;
  mobileProductImage?: ImageType;
  mobileHeroSecondaryImage?: ImageType;
  fullBleedCoverImage?: ImageType;
  cta?: Array<WithCMS<AppType.Content>>;
  desktopBannerHeader?: string;
  desktopBannerBody?: string;
  desktopBannerLink?: WithCMS<AppType.Content>;
  headlinePositionVertical?: VerticalPosition;
  headlinePositionHorizontal?: TextAlignment;
  desktopImageHorizontalPosition?: TextAlignment;
}>;

export const HeroModule: React.FC<HeroModuleProps> = ({
  headline,
  subhead,
  imageLayout,
  desktopTabletHeroImage,
  desktopTabletProductImage,
  desktopTabletHeroSecondaryImage,
  mobileHeroImage,
  mobileProductImage,
  mobileHeroSecondaryImage,
  fullBleedCoverImage,
  cta,
  desktopBannerHeader,
  desktopBannerBody,
  desktopBannerLink,
  headlinePositionVertical,
  headlinePositionHorizontal,
  desktopImageHorizontalPosition,
  ...props
}) => {
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;

  const primaryImage = isMobile ? mobileHeroImage : desktopTabletHeroImage;
  const productImage = isMobile ? mobileProductImage : desktopTabletProductImage;
  const secondaryImage = isMobile ? mobileHeroSecondaryImage : desktopTabletHeroSecondaryImage;
  const isFullBleed = imageLayout === HeroImageVariant.FULLBLEED;

  const getDesktopGridSize = () => {
    if (isFullBleed) {
      return 4;
    }
    if (
      headlinePositionVertical !== VerticalPosition.MIDDLE ||
      desktopImageHorizontalPosition === TextAlignment.CENTER
    ) {
      return 8;
    }
    return 5;
  };

  const headerClassNames = cx(
    {
      [styles.heroHeaderContainerFullbleed]: isFullBleed,
      [styles.heroHeaderContainerTextCentered]: headlinePositionHorizontal === TextAlignment.CENTER,
      [styles.heroHeaderContainerCenter]: desktopImageHorizontalPosition === TextAlignment.CENTER,
      [styles.heroHeaderContainerRight]: desktopImageHorizontalPosition === TextAlignment.RIGHT,
      [styles.heroHeaderContainerTop]: headlinePositionVertical === VerticalPosition.TOP,
    },
    styles.heroHeaderContainer,
  );

  const containerClassNames = cx(
    {
      [styles.heroFullbleed]: isFullBleed,
      [styles.heroFullbleedMiddle]: isFullBleed && headlinePositionVertical === VerticalPosition.MIDDLE,
      [styles.heroFullbleedBottom]: isFullBleed && headlinePositionVertical === VerticalPosition.BOTTOM,
      [styles.heroFullbleedTop]: isFullBleed && headlinePositionVertical === VerticalPosition.TOP,
    },
    styles.hero,
  );

  const renderCTAs = () => (
    <div
      className={cx(styles.heroCtaContainer, {
        [styles.heroCtaContainerCenter]: headlinePositionHorizontal === TextAlignment.CENTER,
      })}
    >
      {cta?.map((item, index) => {
        const { cta: link, ctaColor } = item;
        if (!link) return;

        const ctaClassName = cx(
          {
            [styles.heroCtaWhite]: ctaColor === HeroCtaVariant.WHITE,
            [styles.heroCtaDarkGreen]: ctaColor === HeroCtaVariant.DARK_GREEN,
            [styles.heroCtaFullBleedPrimary]: isFullBleed && index === 0,
            [styles.heroCtaFullBleedSecondary]: isFullBleed,
          },
          styles.heroCta,
        );

        return (
          <ContentLinkV2
            entry={toEntry(link)}
            key={componentKey(link, index)}
            className={ctaClassName}
            hideTitle
          >
            <TextV2 variant="body-medium" tagName="p" value={link.title} />
          </ContentLinkV2>
        );
      })}
    </div>
  );

  return (
    <section {...props._attributes}>
      <Grid container className={containerClassNames}>
        <HeroModuleImage
          imageLayout={imageLayout}
          primaryImage={primaryImage}
          productImage={productImage}
          secondaryImage={secondaryImage}
          fullBleedImage={fullBleedCoverImage}
          horizontalPosition={desktopImageHorizontalPosition}
          verticalPosition={headlinePositionVertical}
        />
        {isFullBleed && <Grid item lg={1} />}
        <Grid item xs={12} md={isFullBleed ? 8 : 12} lg={getDesktopGridSize()} className={headerClassNames}>
          <TextV2
            value={headline}
            variant="h1-medium"
            tagName="h1"
            className={cx(styles.heroHeader, { [styles.heroHeaderFullBleed]: isFullBleed })}
          />
          {subhead && (
            <TextV2
              value={subhead}
              variant="body"
              tagName="p"
              className={cx(styles.heroSubhead, { [styles.heroSubheadFullBleed]: isFullBleed })}
            />
          )}
          {cta && renderCTAs()}
        </Grid>
        {isFullBleed && (
          <HeroModuleBanner header={desktopBannerHeader} body={desktopBannerBody} link={desktopBannerLink} />
        )}
      </Grid>
    </section>
  );
};

HeroModule.displayName = 'HeroModule';
